<template>
    <div>
        <b-container>
            <b-row><b-col>
                <h4>{{ $t('traffic_deviations.canceled_departures.block') }}</h4>
            </b-col><b-col>
                <SelectList 
                :placeholder="datePlaceholder"
                :options="dateOptions"  
                @select="onSelectDate" 
                :searchable="false" 
                :clearable="false" 
                :disabled="loading"
                :value="selectedDate" />
            </b-col></b-row>
            <b-row><b-col>
                <b-form-input type="search" class="string-filter" v-model="blockFilter" :placeholder="$t('traffic_deviations.canceled_departures.search_by_block')" debounce="300" ></b-form-input>
            </b-col></b-row>
            <b-row><b-col>
                <b-form-input type="search" class="string-filter" v-model="vehicleFilter" :placeholder="$t('traffic_deviations.canceled_departures.search_by_vehicle')" debounce="300" ></b-form-input>
            </b-col></b-row>
            <b-row><b-col>
                <input type="checkbox" id="cb-canceled-block-sequences-only" v-model="canceledBlockSequencesOnly" />
                <label :for="'cb-canceled-block-sequences-only'">{{ $t('traffic_deviations.canceled_departures.show_canceled_only') }}</label>
            </b-col></b-row>
            <b-row><b-col>
                <b-form-checkbox-group
                id="cb-block-sequence-canceled"
                name="cb-block-sequence-canceled"
                v-model="blockSequencesWithCancellations">
                <b-table
                    :fields="blockSequenceListFields"
                    :items="blockSequenceListItems"
                    selectable
                    select-mode="single"
                    @row-selected="onSelectBlockSequence"
                    :per-page="perPage"
                    :current-page="currentPage">
                    <template v-slot:cell(tripsCanceled)="data">
                        <b-form-checkbox :value="data.item.blockSequenceId" :disabled="true"></b-form-checkbox>
                    </template>
                </b-table>
                </b-form-checkbox-group>
                <TablePagination
                :total-rows="totalRows"
                :per-page="perPage"
                @pageChange="onPageChange"
                ></TablePagination>
            </b-col></b-row>
        </b-container>
        <CanceledDeparturesConfirm
			modalid="confirmDateModal"
            :message="$t('traffic_deviations.canceled_departures.confirm_reset')"
            :value="dateValue"
			id="confirmDateModal"
            ref="confirmDateModal"
			@ok="onDateConfirmed"
		></CanceledDeparturesConfirm>
    </div>
</template>

<script>
import moment from 'moment';
import CanceledDeparturesConfirm from '@/components/Dispatching/TrafficDeviations/CanceledDeparturesConfirm.vue';
import { haveCancellationsBeenModified } from '@/store/dispatching/trafficDeviations.js';

export default {
    name: 'BlockSequenceTable',
    components: { CanceledDeparturesConfirm },
    data() {
        return {
            loading: true,
            perPage: 15,
            currentPage: 1,
            blockSequencesWithCancellations: [],
            dateToSelect: '',
            blockFilter: '',
            vehicleFilter: '',
            canceledBlockSequencesOnly: false
        };
    },
    computed: {
        datePlaceholder() {
            return this.$t('traffic_deviations.canceled_departures.date_placeholder');
        },
        dateOptions() {
            let options = [];
            for (const date of this.$store.state.trafficDeviations.dateList) {
               options.push({ id: date, label: date, icon: "calendar2-date" });                
            }
            return options;
        },
        selectedDate() {
            return this.$store.state.trafficDeviations.selectedDate;
        },
        dateValue() {
            return this.dateToSelect;
        },
        blockSequenceListFields() {
            return [
                { key: 'tripsCanceled', label: this.$t('traffic_deviations.canceled_departures.cancellations'), sortable: false },
                { key: 'blockLabel', label: this.$t('traffic_deviations.canceled_departures.block_label'), sortable: false },
                { key: 'time', label: this.$t('traffic_deviations.canceled_departures.time'), sortable: false, formatter: (value, key, item) => 
                    this.getTimeFromTimestamp(item.startTime) + '\xa0- ' + this.getTimeFromTimestamp(item.endTime)
                },
                { key: 'vehicleLabel', label: this.$t('traffic_deviations.canceled_departures.vehicle_number'), sortable: false }
            ];
        },
        blockSequenceListItems() {
            let blockList = this.filterBlockListByBlock(this.$store.state.trafficDeviations.blockList);
            blockList = this.filterBlockListByVehicle(blockList);
            let blockSequenceList = this.getFilteredBlockSequencesFromBlocks(blockList);

            if (this.canceledBlockSequencesOnly)
                blockSequenceList = blockSequenceList.filter(blockSequence => blockSequence.tripsCanceled == 'true');

            return blockSequenceList;
        },
        totalRows() {
            return this.blockSequenceListItems ? this.blockSequenceListItems.length : 0;
        },
    },
    beforeCreate() {
        this.$store.dispatch('trafficDeviations/getDateList').finally(() => {
            this.loading = false;
        });
    },
    methods: {
        getTimeFromTimestamp(timestamp) {
            let time = moment.parseZone(timestamp);
            if (time.isValid())
                return time.format('HH:mm');

            return '';
        },
        filterBlockListByBlock(blockList) {
            if (!this.blockFilter || this.blockFilter.length == 0)
                return blockList;

            return blockList.filter(block => block.blockLabel.indexOf(this.blockFilter) == 0);
        },
        filterBlockListByVehicle(blockList) {
            if (!this.vehicleFilter || this.vehicleFilter.length == 0)
                return blockList;

            return blockList.filter(block => {
                if (!Array.isArray(block.blockSequences))
                    return false;
                
                for (const blockSequence of block.blockSequences) {
                    if (blockSequence.vehicleLabel && blockSequence.vehicleLabel.indexOf(this.vehicleFilter) == 0)
                        return true;
                }
                return false;
            });
        },
        getFilteredBlockSequencesFromBlocks(blockList) {
            let resultList = [];
            for (const block of blockList) {
                if (!Array.isArray(block.blockSequences))
                    continue;

                let blockSequencesWithDepartures = this.getBlockSequencesWithDepartures(block.blockSequences);
                let blockSequencesWithVehicle = this.getBlockSequencesWithVehicle(blockSequencesWithDepartures);
                
                let blockSequenceList = blockSequencesWithVehicle.length > 0 ? blockSequencesWithVehicle : blockSequencesWithDepartures;
                
                for (let blockSequence of blockSequenceList) {
                    blockSequence.blockLabel = block.blockLabel;
                    resultList.push(blockSequence);
                }
            }
            return resultList;
        },
        getBlockSequencesWithDepartures(blockSequences) {
            return blockSequences; //Having departures is actually unknown at this time.
        },
        getBlockSequencesWithVehicle(blockSequences) {
            let blockSequencesWithVehicle = [];
            for (const blockSequence of blockSequences) {
                if (blockSequence.vehicleLabel && blockSequence.vehicleLabel.length > 0)
                    blockSequencesWithVehicle.push(blockSequence);
            }
            return blockSequencesWithVehicle;
        },
        onSelectDate(value) {
            if (!this.$UIPrivilege('dispatching/canceleddepartures', 'update')) {
                this.onDateConfirmed(value);
                return;
            }
            if (haveCancellationsBeenModified()) {
                this.dateToSelect = value;
                this.$refs.confirmDateModal.show();
            }
            else {
                this.onDateConfirmed(value);
            }
        },
        onDateConfirmed(value) {
            this.$store.commit('trafficDeviations/updateSelectedDate', value);
            this.loading = true;
            this.$store.dispatch('trafficDeviations/getBlockList', value).finally(() => {
                this.loading = false;
            });
        },
        onSelectBlockSequence(value) {
            if (value[0]) {
                this.$store.commit('trafficDeviations/updateSelectedBlockSequence', value[0]);
            }
        },
        onPageChange(page) {
            this.currentPage = page
        },
        onFiltered() {
            this.currentPage = 1;
        }
    },
    watch: {
        blockSequenceListItems(blockSequenceList) {
            let canceled = [];
            if (Array.isArray(blockSequenceList)) {
                for (const blockSequence of blockSequenceList) {
                    if (blockSequence.tripsCanceled == 'true')
                        canceled.push(blockSequence.blockSequenceId);
                }
            }
            this.blockSequencesWithCancellations = canceled;
        },
        blockFilter() {
            this.onFiltered();
        },
        vehicleFilter() {
            this.onFiltered();
        },
        canceledBlockSequencesOnly() {
            this.onFiltered();
        }
    }
}
</script>

<style scoped lang="scss">

.string-filter {
    margin-bottom: 5px;
}

#cb-canceled-block-sequences-only {
    margin-right: 5px;
}

</style>